/**
 * What the learner app actually reports, and the three rules that keep the
 * numbers on the administrator's screen honest.
 *
 * **1. ONLY ONE THING CREDITS A GIVEN SECOND.** The server computes "time on
 * site" as `sum(duration_ms)` over every event in a visit, whatever its kind.
 * The vocabulary gives two kinds a duration — a heartbeat vouches for the
 * interval the tab was there, a playback event for the interval that played —
 * and both are right, so the client's job is to make sure they never describe
 * the same seconds.
 *
 * That is an ACCOUNTING problem, not a state one, and the difference is where
 * this file's first real bug lived. A boolean "playback is crediting" only
 * describes the instant the heartbeat happens to look at it: playback that ran
 * for thirty seconds and stopped inside a heartbeat's minute left the flag
 * down again by the time the beat arrived, and the beat then vouched for the
 * whole minute — including the half playback had already banked. The mirror
 * case undercounted, because a beat suppressed by the flag threw away the
 * genuinely idle part of its interval too.
 *
 * So playback keeps a RUNNING TOTAL of the milliseconds it has credited, and a
 * heartbeat vouches for `elapsed − credited-since-my-last-beat`. Neither can
 * claim a second the other did, whichever order they happen in.
 *
 * Playback still wins the overlap, and the reason is audio mode. A learner who
 * switches to audio and walks away is a designed case; their tab is hidden, so
 * a visibility-gated heartbeat credits them nothing while they listen to the
 * whole lecture. The playback stream is the only witness to that time, and it
 * keeps running when the tab does not.
 *
 * **2. A HEARTBEAT ONLY EVER VOUCHES FOR TIME THE TAB WAS VISIBLE.** It stops
 * on `visibilitychange` and the next one after a return covers only the
 * interval since that return — and the playback ledger is rebased at the same
 * moment, so time credited while hidden is never subtracted from a visible
 * stretch. A tab left open overnight must not report eight hours of
 * engagement, which is precisely the number an administrator would act on.
 *
 * **3. NOTHING HERE IS ALLOWED TO BREAK A LECTURE.** Every failure path ends
 * in a counter rather than an exception (see `beacon.ts`), and the provider
 * renders its children whether or not any of this works.
 *
 * PRESENCE NAMES ITS COURSE. Every report on the administrator's surface is
 * per course and scans `engagement_events` on `course_id`, so an event with no
 * course contributes to nothing — not the minutes, not the session count, not
 * `last_active_at`, which is the input the `inactive` at-risk reason is
 * computed from. Heartbeats and page views emitted from a course-scoped route
 * therefore name that course; a learner who reads a syllabus every day and
 * never presses play was otherwise reported as sixteen days inactive.
 *
 * SIGNED-IN ONLY. `/v1/me/events` is authenticated, and posting from the
 * landing page would spend a round trip to be told so. The beacon is created
 * when an identity appears and disposed when it goes.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useRouterState } from '@tanstack/react-router';
import { ApiError, postEvents, useIdentity } from '@cusp/api-client';
import { demoActive, recordLearnerDemoEvent } from '@cusp/api-client/demo';
import type { ClientIngestableEventKind } from '@cusp/core';
import { EventBeacon } from './beacon.js';
import { endVisit, nextEventId, visitKey } from './visit.js';

/** How often the tab says "still here", while visible. */
export const HEARTBEAT_MS = 60_000;

/**
 * The most one beat may ever vouch for.
 *
 * `Date.now()` is not a stopwatch. A laptop that sleeps with the tab in front
 * wakes to an interval of eight hours, and the beat that closes it would
 * report every one of them as presence — the server's five-minute clamp would
 * then turn "asleep all night" into five honest-looking minutes on the lead
 * score. A beat covers its own interval; twice that is generous room for a
 * throttled timer and still refuses to believe in a suspended machine.
 */
const MAX_BEAT_MS = HEARTBEAT_MS * 2;

/**
 * A 4xx is the server saying this batch will never be accepted — a course the
 * learner's entitlement has lapsed on, a payload a newer server rejects — and
 * retrying it forever would park it at the head of the queue in front of every
 * later observation. 408 and 429 are the two that genuinely mean "again,
 * later".
 */
function isPermanentFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429;
}

/**
 * The course a path is about, when it is about one.
 *
 * Validated against the wire's own slug pattern rather than trusted: a path
 * segment is whatever the address bar contains, and a malformed one would make
 * the whole batch it travelled in unprocessable — costing the honest events
 * sitting beside it.
 */
const COURSE_PATH = /^\/courses\/([^/]+)/;
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function courseSlugFromPath(pathname: string): string | undefined {
  const segment = COURSE_PATH.exec(pathname)?.[1];
  if (segment === undefined) return undefined;
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    return undefined;
  }
  return SLUG.test(decoded) ? decoded : undefined;
}

export interface EmitOptions {
  courseSlug?: string;
  lectureKey?: string;
  /** The playhead, for playback events. Never a claim about elapsed time. */
  positionMs?: number;
  /**
   * Elapsed time this event vouches for. A heartbeat and a playback sample may
   * both carry it; between them the playback ledger (rule 1) is what stops the
   * same seconds being claimed twice.
   */
  durationMs?: number;
  metadata?: Record<string, unknown>;
}

export type Emit = (kind: ClientIngestableEventKind, options?: EmitOptions) => void;

export interface Telemetry {
  emit: Emit;
  /**
   * The visit these events belong to. Handed out so that a consumer keeping
   * state ACROSS renders — the player's readings of the playhead — can say
   * which visit that state was gathered in, without comparing the identity of
   * a memoised function to answer a question about identity.
   */
  visitKey: string;
  /**
   * Register something that is holding an interval it has not reported yet.
   *
   * THE HEARTBEAT ASKS BEFORE IT COUNTS. Rule 1 is arithmetic over a ledger,
   * and the arithmetic is only right if the ledger is up to date at the moment
   * a beat closes its interval — the player samples the playhead every half
   * minute, so at any instant it is holding up to thirty unreported seconds
   * that the beat would otherwise vouch for as presence and the player would
   * then report as watching. Both, for the same seconds.
   *
   * Two `pagehide` listeners could not have settled this between themselves:
   * their order is decided by which component mounted first, which is the
   * provider, always. So the beat asks, rather than racing.
   *
   * Returns the unregister.
   */
  onClosing: (report: () => void) => () => void;
  /**
   * Milliseconds playback has credited so far this visit — monotonic, and read
   * by the heartbeat as a ledger rather than a flag (rule 1). A ref rather
   * than state: the heartbeat reads it from inside an interval, and
   * re-rendering the whole tree every few seconds to move a number nothing
   * draws would be absurd.
   */
  playbackCreditedMs: { current: number };
}

/** A no-op telemetry, so a component outside the provider is not a crash. */
const NOOP: Telemetry = {
  emit: () => undefined,
  visitKey: '',
  onClosing: () => () => undefined,
  playbackCreditedMs: { current: 0 },
};

const TelemetryContext = createContext<Telemetry>(NOOP);

/** The emitter for the current visit. Safe to call from anywhere, always. */
export function useEmit(): Emit {
  return useContext(TelemetryContext).emit;
}

/** The whole seam, for the player — which both emits and credits. */
export function useTelemetry(): Telemetry {
  return useContext(TelemetryContext);
}

export function TelemetryProvider(props: { children: ReactNode }) {
  const identity = useIdentity();
  const scope = identity ? `${identity.tenantId}:${identity.userId}` : null;

  /*
   * CREATED DURING RENDER, NOT IN AN EFFECT, and that is not a style choice.
   * React runs a CHILD's effects before its parent's, so `VisitSignals` below
   * emits `session_started` and the first `page_view` before a parent effect
   * would have built anything to emit into — and every visit silently lost its
   * first two events. Caught by driving a real browser at a real API and
   * finding only heartbeats in the request log.
   */
  // Memoised with the beacon below, not recomputed per render: it reads (and
  // on a first sighting writes) `sessionStorage`, and this component re-renders
  // on every navigation.
  const key = useMemo(() => (scope === null ? '' : visitKey(scope)), [scope]);
  const beacon = useMemo(
    () =>
      identity === null || scope === null
        ? null
        : new EventBeacon({
            sessionKey: key,
            /*
             * PINNED TO THE IDENTITY THAT OPENED THE VISIT. This queue is
             * flushed exactly when its identity is on the way out, and the
             * transport reads the SELECTED membership at request time — so a
             * membership switch would post one institution's engagement under
             * the other's tenant header.
             */
            send: (body, { keepalive }) => postEvents(body, { keepalive, identity }),
            isPermanent: isPermanentFailure,
          }),
    // `scope` IS the identity, narrowed to the two fields a visit belongs to;
    // depending on the object itself would mint a new visit every time the
    // store re-published an equal one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope],
  );

  /*
   * THE REPLACED BEACON IS RETIRED AFTER THE COMMIT, NOT DURING RENDER, and
   * the cleanup FLUSHES WITHOUT DISPOSING. Both halves were bugs first, and
   * both were taught by a real browser rather than by a test.
   *
   * StrictMode runs every effect mount → cleanup → mount to prove it is
   * re-entrant. `useMemo` is NOT re-run for that, so the cleanup and the
   * second mount see the same beacon — and a cleanup that disposed it left the
   * app holding a permanently dead one. Every event after the first flush was
   * dropped in silence: emitted, queued, never sent. It looked exactly like
   * working code, and only counting rows in Postgres found it.
   *
   * Retiring the PREVIOUS beacon is a different question, and it belongs in an
   * effect because disposing anything during render is a side effect React is
   * entitled to throw away — a render begun for a new identity and then
   * abandoned would have killed the beacon the committed tree was still using.
   * Here the effect only ever sees committed renders, and StrictMode's replay
   * finds `previous === beacon` and does nothing.
   */
  const previous = useRef<EventBeacon | null>(beacon);
  useEffect(() => {
    const prior = previous.current;
    previous.current = beacon;
    if (prior !== null && prior !== beacon) {
      prior.flush(true);
      prior.dispose();
    }
  }, [beacon]);

  useEffect(() => {
    if (beacon === null) {
      // Signing out ends the visit: the next sign-in is a new sitting, not a
      // continuation of somebody else's.
      endVisit();
      return;
    }
    return () => beacon.flush(true);
  }, [beacon]);

  /*
   * BOUND TO ITS BEACON, not routed through a ref written during render. The
   * ref was a second, mutable answer to "which visit is this event in" that
   * changed before the render owning it had committed — so a render React went
   * on to discard could still have pointed the committed tree's emitter at a
   * beacon nobody ever adopted. A closure over the memoised beacon cannot: an
   * emitter that never commits is never called.
   */
  const emit = useMemo<Emit>(() => {
    if (beacon === null) return () => undefined;
    return (kind, options = {}) => {
      if (demoActive()) {
        // Demo watch time comes from the media's active ticks, not playhead samples.
        if (kind !== 'video_progress' && kind !== 'audio_progress')
          recordLearnerDemoEvent({ kind, ...options });
        return;
      }
      beacon.record({
        clientEventId: nextEventId(),
        kind,
        occurredAt: new Date().toISOString(),
        ...(options.courseSlug === undefined ? {} : { courseSlug: options.courseSlug }),
        ...(options.lectureKey === undefined ? {} : { lectureKey: options.lectureKey }),
        ...(options.positionMs === undefined
          ? {}
          : { positionMs: Math.max(0, Math.round(options.positionMs)) }),
        ...(options.durationMs === undefined
          ? {}
          : { durationMs: Math.max(0, Math.round(options.durationMs)) }),
        ...(options.metadata === undefined ? {} : { metadata: options.metadata }),
      });
    };
  }, [beacon]);

  const closing = useMemo(() => new Set<() => void>(), []);
  const onClosing = useCallback<Telemetry['onClosing']>(
    (report) => {
      closing.add(report);
      return () => {
        closing.delete(report);
      };
    },
    [closing],
  );

  const playbackCreditedMs = useRef(0);
  const value = useMemo<Telemetry>(
    () => ({ emit, onClosing, visitKey: key, playbackCreditedMs }),
    [emit, onClosing, key],
  );

  return (
    <TelemetryContext.Provider value={value}>
      {beacon === null || scope === null ? null : (
        /*
         * KEYED ON THE IDENTITY. Switching membership in the picker replaces
         * the beacon without unmounting anything, so a `started` ref that
         * survives the switch means the new identity's visit never reports
         * that it began — and its first route is never reported either.
         */
        <VisitSignals
          key={scope}
          emit={emit}
          beacon={beacon}
          credited={playbackCreditedMs}
          closing={closing}
        />
      )}
      {props.children}
    </TelemetryContext.Provider>
  );
}

/**
 * The three signals the app reports on its own behalf: the visit began, a
 * route was entered, and the tab is still here.
 *
 * Split from the provider so its effects re-run on identity change without
 * tearing down the context every render.
 */
function VisitSignals(props: {
  emit: Emit;
  beacon: EventBeacon;
  credited: { current: number };
  closing: Set<() => void>;
}) {
  const { emit } = props;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const courseSlug = courseSlugFromPath(pathname);
  // Read from a ref inside the heartbeat interval, so a navigation does not
  // have to tear the timer down and restart the clock it is measuring.
  const course = useRef<string | undefined>(courseSlug);
  course.current = courseSlug;

  // ONE PER VISIT. A remount inside the same tab is the same sitting — the
  // visit key survives it, and a second `session_started` would make one
  // sitting look like two.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    emit('session_started');
  }, [emit]);

  useEffect(() => {
    // No duration: a page view is a moment, and only the heartbeat vouches
    // for elapsed time (rule 1). It names its course all the same — that is
    // what puts a visit to a course page into that course's session count and
    // its recency.
    emit('page_view', {
      metadata: { path: pathname },
      ...(courseSlug === undefined ? {} : { courseSlug }),
    });
  }, [emit, pathname, courseSlug]);

  const { beacon, credited, closing } = props;
  useEffect(() => {
    let last = Date.now();
    let creditedAt = credited.current;

    /** Start a fresh interval here — both clocks together, or rule 1 breaks. */
    const rebase = (at: number) => {
      last = at;
      creditedAt = credited.current;
    };

    /**
     * Vouch for the interval since the last beat, MINUS whatever playback
     * credited inside it, and only for an interval the tab was visible for.
     *
     * WHO THE INTERVAL BELONGED TO IS AN ARGUMENT, not something to read off
     * the document. By the time `visibilitychange` fires for a tab going away,
     * `visibilityState` is already `hidden` — so a beat that asked the document
     * threw away the visible minute it had been called to bank, which is the
     * whole of a short visit's time on site.
     *
     * Both clocks are rebased either way, so a suppressed beat is never banked
     * and re-claimed by the next one, and `pagehide` after a hide finds nothing
     * left to claim rather than claiming it twice.
     */
    /**
     * Everything holding an unreported interval reports it now, so that the
     * ledger this beat is about to subtract is complete. Nothing here may
     * throw: a beat runs from a timer, from `pagehide`, and from a React
     * cleanup, and rule 3 holds in all three.
     */
    const settle = () => {
      for (const report of closing) {
        try {
          report();
        } catch {
          // A reporter that cannot report is not the lecture's problem.
        }
      }
    };

    const beat = (wasVisible = document.visibilityState === 'visible') => {
      settle();
      const now = Date.now();
      // `Date.now()` is not a stopwatch: a clock correction can run it
      // backwards, and a suspended machine hands back hours.
      const elapsed = Math.min(now - last, MAX_BEAT_MS);
      const played = Math.max(0, credited.current - creditedAt);
      rebase(now);
      if (!wasVisible) return;
      const unaccounted = elapsed - played;
      if (unaccounted > 0) {
        emit('heartbeat', {
          durationMs: unaccounted,
          ...(course.current === undefined ? {} : { courseSlug: course.current }),
        });
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Returning: both clocks restart here, so neither the hidden stretch
        // nor the playback credited inside it is claimed against the visible
        // time that follows.
        rebase(Date.now());
      } else {
        /*
         * Leaving: bank the stretch that WAS visible before it becomes
         * unmeasurable, then send it the way a departure is sent. A hidden tab
         * may never run a timer again, and on the ordinary path to a closed
         * tab `pagehide` follows this by milliseconds — an ordinary request
         * still in the air at that point is cancelled with the document, and
         * it would be carrying exactly the interval just banked.
         */
        beat(true);
        beacon.flush(true);
      }
    };

    const onPageHide = () => {
      beat();
      beacon.flush(true);
    };

    /*
     * A `pagehide` IS NOT ALWAYS A FAREWELL. A page put into the back-forward
     * cache fires one, is frozen — no timers, for minutes — and then comes
     * back on `pageshow` exactly as it was, clocks and all. Without restarting
     * the interval here the first beat after a restore vouched for the whole
     * time the page spent frozen, up to the clamp, as presence that never
     * happened.
     */
    const onPageShow = () => rebase(Date.now());

    const timer = setInterval(() => beat(), HEARTBEAT_MS);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      /*
       * THE DEPARTING IDENTITY KEEPS ITS LAST MINUTE. Signing out or switching
       * membership unmounts this component between beats, and without closing
       * the interval here everything since the last one simply vanished — into
       * nothing, because the next mount rebases its clock. `beat` emits through
       * the emitter bound to THIS beacon, so the time lands in the visit it was
       * spent in rather than in the one that replaced it.
       */
      beat();
      beacon.flush(true);
    };
    // Stable by construction: `emit` is bound to `beacon`, and `credited` is a
    // ref object. A dependency on `props` itself would restart the clock — and
    // run the cleanup's beat — on every render.
  }, [emit, beacon, credited, closing]);

  return null;
}
